import { FlexBox, IconNames, Tag } from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import type { Tag as TagT } from 'lib-shared-legacy-types';
import memoize from 'memoize-one';
import { v4 as uuid } from 'uuid';

import { FlowNode, flowNodeColor, NodeTypeComponent } from 'fable/flowEditor';
import type { InternalPortT, NodeT, RegisterPortT } from 'fable/flowEditor';

import ActionModal from '../../containers/modals/tags';
import { formatPortsHelper } from '../../helpers/nodePortFormatter';
import { FlowNodeTypes } from '../types';

import styles from './tags.module.scss';

export type FlowNodeTagPayloadT = {
  next_node_id: string;
  tag_ids: string[];
};

export type FlowNodeTagT = NodeT<FlowNodeTypes.TAGS, FlowNodeTagPayloadT>;

type StateT = { nodeOverflowing: number };

const portTranslator = (node: FlowNodeTagT): RegisterPortT[] => {
  if (node.payload.next_node_id) {
    return [
      {
        id: uuid(),
        type: 'out',
        label: 'arrow',
        connection: node.payload.next_node_id,
      },
    ];
  }

  return [];
};

export class TagsNode extends NodeTypeComponent<
  FlowNodeTagT,
  { tags: TagT[] },
  StateT
>() {
  static icon: IconNames = 'tag';
  static label = 'Tags';
  static helpText =
    'Automatically apply custom tags to gain insights about your customer conversations (e.g. identify your VIP customers). ';
  static color = flowNodeColor.blue;
  static translatePayload = formatPortsHelper(portTranslator);
  static ActionModal = ActionModal;
  static actionTrigger = { type: FlowNodeTypes.TAGS };
  static updateConnection = (
    node: FlowNodeTagT,
    _port: InternalPortT,
    newConnection: string,
  ): FlowNodeTagT => {
    const payload: FlowNodeTagPayloadT = { ...node.payload };
    payload.next_node_id = newConnection;

    return { ...node, payload };
  };

  @boundMethod
  triggerEdit(): void {
    const { onEdit, node } = this.props;
    onEdit(node, node.nodeType);
  }

  resolveTags = memoize((tags: TagT[], tagIDs: string[]) =>
    tags.filter((t) => tagIDs.includes(t.name)),
  );

  render() {
    const {
      node: {
        payload: { tag_ids },
      },
      extraData: { tags },
    } = this.props;

    const nodeTags: TagT[] = this.resolveTags(tags, tag_ids);
    const { color, name } = nodeTags?.[0] || {};

    return (
      <FlowNode
        color={TagsNode.color}
        icon={TagsNode.icon}
        label={TagsNode.label}
        text={
          <FlexBox alignItems="center" justifyContent="space-between">
            <Tag key={name} className={styles.tag} color={color} noBorder>
              {name}
            </Tag>
            {nodeTags.length > 1 && (
              <span className={styles.tagsNumber}>+{nodeTags.length - 1}</span>
            )}
          </FlexBox>
        }
        onEdit={this.triggerEdit}
      />
    );
  }
}
